import axios, { AxiosInstance } from "axios";
import axiosRetry from "axios-retry";
import querystring from "querystring";
import { config } from "config";
import { Paths } from "Paths";
import * as CommonConstants from "utilities/CommonConstants";

const LIMIT_RETRY_COUNT = config.limitRetryCount;
const RETRY_DELAY = config.retryDelay;

export interface ApiClientResponse {
    status: number;
    data: any;
}

export const unknownErrorResponse = {
    status: -500,
    errors: [
        {
            error_code: 999999,
            error_message: `unexpected error.`,
        },
    ],
};

/**
 * ポーリング用のタイマー設定
 */
export function waitPollingTime(ms: number): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export class ApiClient {
    axiosInstance: AxiosInstance;

    /**
     * Constructor
     */
    constructor() {
        this.axiosInstance = axios.create();

        /**
         * axiosRetryのデフォルト動作として
         * レスポンスコードが500 or ネットワークエラーの場合にリトライします
         */
        axiosRetry(this.axiosInstance, {
            retryDelay: (LIMIT_RETRY_COUNT) => {
                return RETRY_DELAY;
            },
        });
    }

    /**
     * APIURL取得
     */
    private getApiUrl(): string {
        return config.apiUrl;
    }

    /**
     * ベースURL取得
     */
    private getBaseUrl(): string {
        return config.baseUrl;
    }

    /**
     * リクエストヘッダー取得
     */
    private async getRequestHeaders() {
        const token = await this.getToken();

        return {
            Authorization: `Bearer ${token}`,
        };
    }

    /**
     * GET
     */
    public async get<T>(path: string, params = {}): Promise<ApiClientResponse> {
        const headers = await this.getRequestHeaders();
        return this.request({
            method: `GET`,
            url: this.getApiUrl() + path,
            params: params,
            headers: headers,
        });
    }

    /**
     * POST
     */
    public async post<T>(path: string, payload = {}): Promise<ApiClientResponse> {
        const headers = await this.getRequestHeaders();
        return this.request({
            method: `POST`,
            url: this.getApiUrl() + path,
            headers: headers,
            responseType: `json`,
            data: payload,
        });
    }

    /**
     * PUT
     */
    public async put<T>(path: string, payload = {}): Promise<ApiClientResponse> {
        const headers = await this.getRequestHeaders();
        return this.request({
            method: `PUT`,
            url: this.getApiUrl() + path,
            headers: headers,
            responseType: `json`,
            data: payload,
        });
    }

    /**
     * DELETE
     */
    public async delete<T>(path: string, params = {}): Promise<ApiClientResponse> {
        const headers = await this.getRequestHeaders();
        return this.request({
            method: `DELETE`,
            url: this.getApiUrl() + path,
            headers: headers,
            responseType: `json`,
            params: params,
        });
    }

    /**
     * トークン取得
     */
    private async getToken() {
        let token = sessionStorage.getItem(CommonConstants.SESSION_KEY_TOKEN);
        if (token === `` || token === null) {
            const response = await axios.post(
                config.tokenUrl,
                querystring.stringify({ grant_type: `client_credentials` }),
                {
                    headers: {
                        Authorization: ` Basic ${btoa(config.clientId + `:` + config.clientSecret)}`,
                        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
                    },
                }
            );

            token = response.data.token;
            sessionStorage.setItem(CommonConstants.SESSION_KEY_TOKEN, token || ``);
        }

        return token;
    }

    /**
     * リクエスト/レスポンスの共通処理
     *
     * @param options
     */
    private request(options: any) {
        const onSuccess = (response: any) => {
            if (response) {
                if (response.data.updateToken) {
                    sessionStorage.setItem(CommonConstants.SESSION_KEY_TOKEN, response.headers.authorization);
                }

                return Promise.resolve({
                    status: response.status,
                    data: response.data,
                });
            }

            return Promise.reject(unknownErrorResponse);
        };

        const onError = (error: any) => {
            if (error.response) {
                // トークン切れの場合は403が返却される為初期化
                if (401 === error.response.status) {
                    // トークン切れ
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_TOKEN);
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_CONFIG);
                    window.location.href = this.getBaseUrl() + `/#` + Paths.signin;
                } else if (403 === error.response.status) {
                    // 認証エラー
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_TOKEN);
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_CONFIG);
                    window.location.href = this.getBaseUrl() + `/#` + Paths.signin;
                } else if (400 === error.response.status) {
                    // 入力チェックエラー
                    // 何もしない
                } else {
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_TOKEN);
                    sessionStorage.removeItem(CommonConstants.SESSION_KEY_CONFIG);
                    window.location.href = this.getBaseUrl() + `/#` + Paths.systemBusy;
                }

                return Promise.reject({
                    status: error.response.status,
                    errors: error.response.data ? error.response.data : [],
                });
            }

            // 想定外のネットワークエラーの場合もトークンを初期化
            sessionStorage.removeItem(CommonConstants.SESSION_KEY_TOKEN);
            sessionStorage.removeItem(CommonConstants.SESSION_KEY_CONFIG);
            window.location.href = this.getBaseUrl() + `/#` + Paths.systemBusy;

            return Promise.reject(unknownErrorResponse);
        };

        return this.axiosInstance(options).then(onSuccess).catch(onError);
    }
}

export const apiClient = new ApiClient();
